<template>
  <div
      class="cws-container cws-sidebar-right blog-wrapper"
  >

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row>
        <b-col cols="12">
          <b-card>
            <b-overlay
                :show="isLoadingInfo"
                rounded="sm"
            >
            <!-- Track info -->
            <div v-if="trackInfo">
              <h4>
                Трек
                <b-button
                    v-if="trackInfo.artist_id"
                    variant="flat-primary"
                    size="sm"
                    class="ml-50"
                    :to="{name: 'artist-view', params: { id: trackInfo.artist_id }}"
                >
                  {{ trackInfo.artist }}
                  <feather-icon icon="ExternalLinkIcon" size="14" class="ml-25"/>
                </b-button>
              </h4>
              <b-card class="mb-2" border-variant="primary">
                <h5 class="d-flex align-items-center">
                  <b-button
                      v-if="trackInfo.file"
                      variant="success"
                      class="btn-icon mr-1"
                      size="sm"
                      :href="trackInfo.url"
                  >
                    <feather-icon icon="SaveIcon"/>
                  </b-button>
                  <b-button
                      v-else
                      variant="flat-danger"
                      class="btn-icon mr-1"
                      size="sm"
                      disabled
                  >
                    <feather-icon icon="AlertCircleIcon"/>
                  </b-button>
                  <template v-if="!isEditingTitle">
                    {{ trackInfo.artist }} — {{ trackInfo.track }}
                    <b-button
                        variant="flat-primary"
                        class="btn-icon ml-50"
                        size="sm"
                        @click="startEditingTitle"
                    >
                      <feather-icon icon="Edit2Icon"/>
                    </b-button>
                    <b-badge v-if="trackInfo.is_cover" variant="warning" class="ml-50">кавер</b-badge>
                    <b-badge v-if="trackInfo.has_covers" variant="success" class="ml-50">оригинал</b-badge>
                    <find-original-button
                        :query="`${trackInfo.artist} - ${trackInfo.track}`"
                        store-namespace="artists"
                        class="ml-50"
                    />
                  </template>
                  <template v-else>
                    <b-form-input
                        v-model="editTitle"
                        class="mr-1"
                        style="max-width: 400px;"
                        @keyup.enter="saveTitle"
                    />
                    <b-button
                        variant="success"
                        class="btn-icon mr-50"
                        size="sm"
                        @click="saveTitle"
                    >
                      <feather-icon icon="CheckIcon"/>
                    </b-button>
                    <b-button
                        variant="flat-secondary"
                        class="btn-icon"
                        size="sm"
                        @click="cancelEditingTitle"
                    >
                      <feather-icon icon="XIcon"/>
                    </b-button>
                  </template>
                </h5>
                <div class="d-flex align-items-center flex-wrap">
                  <span class="mr-2 d-inline-flex align-items-center">
                    Прослушивания:
                    <template v-if="!isEditingPlayCount">
                      <span class="ml-25">{{ formatNumber(trackInfo.play_count) }}</span>
                      <b-button
                          variant="flat-primary"
                          class="btn-icon ml-25 p-25"
                          size="sm"
                          @click="startEditingPlayCount"
                      >
                        <feather-icon icon="Edit2Icon" size="12"/>
                      </b-button>
                    </template>
                    <template v-else>
                      <b-form-input
                          v-model="editPlayCount"
                          type="number"
                          size="sm"
                          class="ml-50"
                          style="width: 140px;"
                          @keyup.enter="savePlayCount"
                      />
                      <b-button
                          variant="success"
                          class="btn-icon ml-25"
                          size="sm"
                          @click="savePlayCount"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                      <b-button
                          variant="flat-secondary"
                          class="btn-icon"
                          size="sm"
                          @click="isEditingPlayCount = false"
                      >
                        <feather-icon icon="XIcon"/>
                      </b-button>
                    </template>
                  </span>
                  <span class="mr-2 d-inline-flex align-items-center">
                    Год выпуска:
                    <template v-if="!isEditingYear">
                      <span class="ml-25">{{ trackInfo.release_year || '—' }}</span>
                      <b-button
                          variant="flat-primary"
                          class="btn-icon ml-25 p-25"
                          size="sm"
                          @click="startEditingYear"
                      >
                        <feather-icon icon="Edit2Icon" size="12"/>
                      </b-button>
                      <b-button
                          v-b-tooltip.hover
                          :title="yearRefreshSource
                            ? 'Обновить из ' + externalSourceLabels[yearRefreshSource]
                            : 'Нет привязанного Spotify, SecondHandSongs или Yandex ID'"
                          variant="flat-primary"
                          class="btn-icon p-25"
                          size="sm"
                          :disabled="!yearRefreshSource || isRefreshingYear"
                          @click="refreshYear"
                      >
                        <b-spinner v-if="isRefreshingYear" small style="width: 12px; height: 12px;"/>
                        <feather-icon v-else icon="RefreshCwIcon" size="12"/>
                      </b-button>
                    </template>
                    <template v-else>
                      <b-form-select
                          v-model="editYear"
                          :options="yearOptions"
                          size="sm"
                          class="ml-50"
                          style="width: 100px;"
                      />
                      <b-button
                          variant="success"
                          class="btn-icon ml-25"
                          size="sm"
                          @click="saveYear"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                      <b-button
                          variant="flat-secondary"
                          class="btn-icon"
                          size="sm"
                          @click="isEditingYear = false"
                      >
                        <feather-icon icon="XIcon"/>
                      </b-button>
                    </template>
                  </span>
                  <div class="ml-md-auto">
                    <rating-stats-block
                        :rating="trackInfo.difficulty_rating"
                        :calculated="trackInfo.calculated_difficulty"
                        :lastfm="trackInfo.play_count"
                        :spotify="trackInfo.spotify_playcount"
                        refreshable
                        :refreshing="platformStatsSyncing"
                        @refresh="syncPlatformStats"
                    />
                  </div>
                </div>
                <div class="mt-50 d-flex flex-column align-items-start" style="gap: 0.25rem;">
                  <small class="text-muted">Внешние ID:</small>

                  <!-- Spotify -->
                  <span v-if="trackInfo.external_ids && trackInfo.external_ids.spotify" class="d-inline-flex align-items-center mr-25">
                    <b-badge
                        tag="a"
                        :href="'https://open.spotify.com/track/' + trackInfo.external_ids.spotify"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="success"
                        class="cursor-pointer"
                        style="font-size: 0.7rem; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    >
                      {{ externalTitle('spotify') || trackInfo.external_ids.spotify }}
                      <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                    </b-badge>
                    <b-button
                        variant="flat-danger"
                        class="btn-icon p-0"
                        size="sm"
                        style="line-height: 1; margin-left: -2px;"
                        @click="deleteExternalId('spotify')"
                    >
                      <feather-icon icon="XIcon" size="11" />
                    </b-button>
                  </span>
                  <b-button
                      v-else
                      variant="outline-success"
                      size="sm"
                      class="py-25 px-50 font-small-2 mr-25"
                      :disabled="!artistExternalIds.spotify"
                      @click="syncExternalId('spotify')"
                  >
                    <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                    Spotify
                  </b-button>

                  <!-- Yandex Music -->
                  <span v-if="trackInfo.external_ids && trackInfo.external_ids.yandex" class="d-inline-flex align-items-center mr-25">
                    <b-badge
                        tag="a"
                        :href="'https://music.yandex.ru/track/' + trackInfo.external_ids.yandex"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="cursor-pointer badge-yandex"
                        style="font-size: 0.7rem; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    >
                      {{ externalTitle('yandex') || trackInfo.external_ids.yandex }}
                      <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                    </b-badge>
                    <b-button
                        variant="flat-danger"
                        class="btn-icon p-0"
                        size="sm"
                        style="line-height: 1; margin-left: -2px;"
                        @click="deleteExternalId('yandex')"
                    >
                      <feather-icon icon="XIcon" size="11" />
                    </b-button>
                  </span>
                  <b-button
                      v-else
                      variant="outline-yandex"
                      size="sm"
                      class="py-25 px-50 font-small-2 mr-25 btn-outline-yandex"
                      :disabled="!artistExternalIds.yandex"
                      @click="syncExternalId('yandex')"
                  >
                    <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                    Yandex
                  </b-button>

                  <!-- YouTube -->
                  <span v-if="trackInfo.external_ids && trackInfo.external_ids.youtube" class="d-inline-flex align-items-center mr-25">
                    <b-badge
                        v-b-tooltip.hover
                        :title="'YouTube: ' + trackInfo.external_ids.youtube"
                        tag="a"
                        :href="'https://www.youtube.com/watch?v=' + trackInfo.external_ids.youtube"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="danger"
                        class="cursor-pointer"
                        style="font-size: 0.7rem; background-color: #ff0000 !important; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    >{{ externalTitle('youtube') || 'YT' }}</b-badge>
                    <b-button
                        variant="flat-danger"
                        class="btn-icon p-0"
                        size="sm"
                        style="line-height: 1; margin-left: -2px;"
                        @click="deleteExternalId('youtube')"
                    >
                      <feather-icon icon="XIcon" size="11" />
                    </b-button>
                  </span>
                  <b-button
                      v-else
                      variant="outline-danger"
                      size="sm"
                      class="py-25 px-50 font-small-2 mr-25"
                      disabled
                      style="border-color: #ff0000 !important; color: #ff0000 !important;"
                  >
                    <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                    YouTube
                  </b-button>

                  <!-- SecondHandSongs -->
                  <span v-if="trackInfo.external_ids && trackInfo.external_ids.second_hand_songs" class="d-inline-flex align-items-center mr-25">
                    <b-badge
                        tag="a"
                        :href="'https://secondhandsongs.com/performance/' + trackInfo.external_ids.second_hand_songs"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="cursor-pointer shs-button"
                        style="font-size: 0.7rem; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    >
                      {{ externalTitle('second_hand_songs') || trackInfo.external_ids.second_hand_songs }}
                      <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                    </b-badge>
                    <b-button
                        variant="flat-danger"
                        class="btn-icon p-0"
                        size="sm"
                        style="line-height: 1; margin-left: -2px;"
                        @click="deleteExternalId('second_hand_songs')"
                    >
                      <feather-icon icon="XIcon" size="11" />
                    </b-button>
                  </span>
                  <b-button
                      v-else
                      variant="outline-info"
                      size="sm"
                      class="py-25 px-50 font-small-2 mr-25 badge-outline-shs"
                      :disabled="!artistExternalIds.second_hand_songs"
                      @click="syncExternalId('second_hand_songs')"
                  >
                    <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                    SHS
                  </b-button>

                  <b-button
                      v-b-tooltip.hover
                      title="Добавить внешний ID"
                      variant="flat-primary"
                      class="btn-icon p-25"
                      size="sm"
                      @click="showAddExternalIdModal = true"
                  >
                    <feather-icon icon="PlusCircleIcon" size="14"/>
                  </b-button>
                </div>

                <!-- Modal: Добавить внешний ID -->
                <b-modal
                    v-model="showAddExternalIdModal"
                    title="Добавить внешний ID"
                    ok-title="Добавить"
                    cancel-title="Отмена"
                    centered
                    :ok-disabled="!externalIdInput.trim() || isAddingExternalId"
                    :busy="isAddingExternalId"
                    @ok.prevent="addExternalId"
                    @hidden="externalIdInput = ''"
                >
                  <b-form-group
                      label="Ссылка или идентификатор"
                      description="Вставьте ссылку на трек (Spotify, YouTube, Yandex Music, SHS) или идентификатор"
                  >
                    <b-form-input
                        v-model="externalIdInput"
                        placeholder="https://open.spotify.com/track/... или ID"
                        autofocus
                        @keyup.enter="addExternalId"
                    />
                  </b-form-group>
                </b-modal>

                <!-- Modal: выбор кандидата внешнего ID -->
                <b-modal
                    v-model="showExternalCandidates"
                    :title="candidatesModalTitle"
                    hide-footer
                    centered
                >
                  <div v-if="externalCandidates.length">
                    <div
                        v-for="candidate in externalCandidates"
                        :key="candidate.id"
                        class="d-flex align-items-center mb-1"
                    >
                      <b-button
                          variant="primary"
                          size="sm"
                          class="mr-1"
                          @click="selectExternalCandidate(candidate)"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                      <span>
                        {{ candidate.artist }} — {{ candidate.title }}
                        <span v-if="candidate.year" class="text-muted">({{ candidate.year }})</span>
                        <b-badge v-if="candidate.is_original === false" variant="light-warning" class="ml-25">кавер</b-badge>
                      </span>
                      <a
                          v-if="candidateUrl(candidate)"
                          :href="candidateUrl(candidate)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="ml-auto"
                      >
                        <feather-icon icon="ExternalLinkIcon"/>
                      </a>
                    </div>
                  </div>
                  <div v-else class="text-muted">Ничего не найдено</div>
                </b-modal>
              </b-card>

              <!-- Audio player -->
              <div v-if="trackInfo.file" class="mb-2">
                <audio
                    controls
                    :src="trackInfo.file_url"
                    style="width: 100%;"
                    preload="none"
                />
              </div>
            </div>
            </b-overlay>

            <!-- Tabs -->
            <b-tabs v-model="activeTabIndex" content-class="mt-1">
              <b-tab title="Скачать">
                <track-detail-search
                    :track-info="trackInfo"
                    @track-updated="fetchTrackInfo"
                />
              </b-tab>
              <b-tab title="Каверы">
                <track-detail-covers
                    :track-id="trackId"
                />
              </b-tab>
            </b-tabs>

          </b-card>
        </b-col>
      </b-row>
    </div>
    <!--/ content -->

  </div>
</template>

<script>
import {
  BFormInput,
  BFormGroup,
  BFormSelect,
  BCard,
  BRow,
  BCol,
  BBadge,
  BButton,
  BTab,
  BTabs,
  BModal,
  BOverlay,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import {formatNumber} from '@core/utils/filter'
import router from "@/router"
import store from "@/store"
import musicStoreModule from "@/views/db-music/musicStoreModule"
import {computed, onUnmounted, ref, watch} from "@vue/composition-api"
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TrackDetailSearch from './TrackDetailSearch.vue'
import TrackDetailCovers from './TrackDetailCovers.vue'
import FindOriginalButton from './FindOriginalButton.vue'
import RatingStatsBlock from './RatingStatsBlock.vue'

export default {
  components: {
    BFormInput,
    BFormGroup,
    BFormSelect,
    BCard,
    BRow,
    BCol,
    BBadge,
    BButton,
    BTab,
    BTabs,
    BModal,
    BOverlay,
    BSpinner,
    TrackDetailSearch,
    TrackDetailCovers,
    FindOriginalButton,
    RatingStatsBlock,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'artists'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const trackInfo = ref(null)
    const isLoadingInfo = ref(false)
    const isEditingTitle = ref(false)
    const editTitle = ref('')
    const isEditingPlayCount = ref(false)
    const editPlayCount = ref(null)
    const isEditingYear = ref(false)
    const editYear = ref(null)
    const isRefreshingYear = ref(false)

    const currentYear = new Date().getFullYear()
    const yearOptions = Array.from({length: currentYear - 1900 + 1}, (_, i) => currentYear - i)
    const showAddExternalIdModal = ref(false)
    const externalIdInput = ref('')
    const isAddingExternalId = ref(false)
    const showExternalCandidates = ref(false)
    const externalCandidates = ref([])
    const externalCandidatesSource = ref(null)

    const externalSourceLabels = {
      spotify: 'Spotify',
      yandex: 'Yandex Music',
      second_hand_songs: 'SecondHandSongs',
    }

    const trackId = computed(() => router.currentRoute.params.id)

    // Tab from query
    const tabMap = { search: 0, covers: 1 }
    const initialTab = tabMap[router.currentRoute.query.tab] || 0
    const activeTabIndex = ref(initialTab)

    const fetchTrackInfo = async () => {
      isLoadingInfo.value = true
      try {
        const res = await store.dispatch('artists/fetchTrackDetails', {id: trackId.value})
        trackInfo.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки данных трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const startEditingTitle = () => {
      editTitle.value = trackInfo.value.title
      isEditingTitle.value = true
    }

    const cancelEditingTitle = () => {
      isEditingTitle.value = false
    }

    const saveTitle = async () => {
      const newTitle = editTitle.value.trim()
      if (!newTitle || newTitle === trackInfo.value.title) {
        isEditingTitle.value = false
        return
      }
      isLoadingInfo.value = true
      try {
        await store.dispatch('artists/updateTrack', {id: trackInfo.value.id, queryParams: {title: newTitle}})
        await fetchTrackInfo()
        isEditingTitle.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Название обновлено',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления названия',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const updateTrackField = async (queryParams, successTitle, errorTitle) => {
      isLoadingInfo.value = true
      try {
        await store.dispatch('artists/updateTrack', {id: trackInfo.value.id, queryParams})
        await fetchTrackInfo()
        toast({
          component: ToastificationContent,
          props: {
            title: successTitle,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        return true
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: errorTitle,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return false
      } finally {
        isLoadingInfo.value = false
      }
    }

    const startEditingPlayCount = () => {
      editPlayCount.value = trackInfo.value.play_count
      isEditingPlayCount.value = true
    }

    const savePlayCount = async () => {
      const newCount = parseInt(editPlayCount.value, 10)
      if (Number.isNaN(newCount) || newCount < 0 || newCount === trackInfo.value.play_count) {
        isEditingPlayCount.value = false
        return
      }
      const ok = await updateTrackField(
        {play_count: newCount},
        'Количество прослушиваний обновлено',
        'Ошибка обновления количества прослушиваний',
      )
      if (ok) isEditingPlayCount.value = false
    }

    const startEditingYear = () => {
      editYear.value = trackInfo.value.release_year || null
      isEditingYear.value = true
    }

    const saveYear = async () => {
      if (!editYear.value || editYear.value === trackInfo.value.release_year) {
        isEditingYear.value = false
        return
      }
      const ok = await updateTrackField(
        {release_year: editYear.value},
        'Год выпуска обновлён',
        'Ошибка обновления года выпуска',
      )
      if (ok) isEditingYear.value = false
    }

    const refreshYear = async () => {
      const source = yearRefreshSource.value
      if (!source) return
      isRefreshingYear.value = true
      try {
        const res = await store.dispatch('artists/fetchTrackExternalReleaseYear', {id: trackId.value, source})
        const year = res.data.year
        if (!year) {
          toast({
            component: ToastificationContent,
            props: {
              title: `Год не найден в ${externalSourceLabels[source]}`,
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
          return
        }
        const currentYearValue = trackInfo.value.release_year
        if (year === currentYearValue) {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Год уже актуален',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          return
        }
        if (currentYearValue) {
          const confirmed = await root.$bvModal.msgBoxConfirm(
            `${externalSourceLabels[source]} вернул ${year}, сейчас указан ${currentYearValue}. Заменить?`,
            {
              title: 'Обновить год выпуска',
              okTitle: 'Заменить',
              cancelTitle: 'Отмена',
              okVariant: 'primary',
              centered: true,
            },
          )
          if (!confirmed) return
        }
        await updateTrackField(
          {release_year: year},
          'Год выпуска обновлён',
          'Ошибка обновления года выпуска',
        )
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка получения года из внешнего сервиса',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isRefreshingYear.value = false
      }
    }

    const candidatesModalTitle = computed(
      () => `Выберите трек в ${externalSourceLabels[externalCandidatesSource.value] || ''}`,
    )

    // Поиск внешнего ID трека доступен, только если этот источник привязан у артиста
    const artistExternalIds = computed(() => (trackInfo.value && trackInfo.value.artist_external_ids) || {})

    // Название трека на площадке (если сохранено), иначе null
    const externalTitle = source => ((trackInfo.value && trackInfo.value.external_titles) || {})[source] || null

    // Источник для обновления года: Spotify → SecondHandSongs → Yandex
    const yearRefreshSource = computed(() => {
      const externalIds = (trackInfo.value && trackInfo.value.external_ids) || {}
      if (externalIds.spotify) return 'spotify'
      if (externalIds.second_hand_songs) return 'second_hand_songs'
      if (externalIds.yandex) return 'yandex'
      return null
    })

    const candidateUrl = candidate => {
      if (candidate.url) return candidate.url
      if (externalCandidatesSource.value === 'spotify') return `https://open.spotify.com/track/${candidate.id}`
      if (externalCandidatesSource.value === 'yandex') return `https://music.yandex.ru/track/${candidate.id}`
      return null
    }

    const syncExternalId = async source => {
      isLoadingInfo.value = true
      try {
        const res = await store.dispatch('artists/syncTrackExternalId', {id: trackId.value, source})
        if (res.data.success) {
          await fetchTrackInfo()
          toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'Внешний ID привязан',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else if (res.data.candidates && res.data.candidates.length) {
          externalCandidates.value = res.data.candidates
          externalCandidatesSource.value = source
          showExternalCandidates.value = true
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || `Не найдено в ${externalSourceLabels[source]}`,
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска внешнего ID',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const selectExternalCandidate = async candidate => {
      showExternalCandidates.value = false
      isLoadingInfo.value = true
      try {
        const res = await store.dispatch('artists/syncTrackExternalId', {
          id: trackId.value,
          source: externalCandidatesSource.value,
          externalId: String(candidate.id),
          year: candidate.year || undefined,
        })
        if (res.data.success) {
          await fetchTrackInfo()
          toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'Внешний ID привязан',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка привязки внешнего ID',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const addExternalId = async () => {
      const url = externalIdInput.value.trim()
      if (!url) return
      isAddingExternalId.value = true
      try {
        const res = await store.dispatch('artists/addTrackExternalId', {id: trackId.value, url})
        showAddExternalIdModal.value = false
        externalIdInput.value = ''
        trackInfo.value.external_ids = res.data.external_ids
        trackInfo.value.external_titles = res.data.external_titles

        // Бэкенд дозаполняет пустой год при привязке Spotify/Yandex — подтягиваем свежие данные
        if (!trackInfo.value.release_year && /open\.spotify|music\.yandex/.test(url)) {
          await fetchTrackInfo()
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка добавления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isAddingExternalId.value = false
      }
    }

    const deleteExternalId = async (source) => {
      isLoadingInfo.value = true
      try {
        const res = await store.dispatch('artists/deleteTrackExternalId', {id: trackId.value, source})
        trackInfo.value.external_ids = res.data.external_ids
        trackInfo.value.external_titles = res.data.external_titles
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка удаления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const platformStatsSyncing = ref(false)

    const syncPlatformStats = async () => {
      platformStatsSyncing.value = true
      try {
        const response = await store.dispatch('artists/syncTrackPlatformStats', {id: trackId.value})
        const trackData = response.data.track && response.data.track.data
          ? response.data.track.data
          : response.data.track
        if (trackData) {
          trackInfo.value.play_count = trackData.play_count
          trackInfo.value.spotify_playcount = trackData.spotify_playcount
          trackInfo.value.calculated_difficulty = trackData.calculated_difficulty
        }
        if (!response.data.success) {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Данные площадок не обновлены',
              text: (response.data.messages || []).join('; '),
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления данных площадок',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        platformStatsSyncing.value = false
      }
    }

    fetchTrackInfo()

    return {
      trackInfo,
      trackId,
      isLoadingInfo,
      isEditingTitle,
      editTitle,
      activeTabIndex,
      formatNumber,
      fetchTrackInfo,
      startEditingTitle,
      cancelEditingTitle,
      saveTitle,
      isEditingPlayCount,
      editPlayCount,
      startEditingPlayCount,
      savePlayCount,
      isEditingYear,
      editYear,
      yearOptions,
      startEditingYear,
      saveYear,
      isRefreshingYear,
      yearRefreshSource,
      externalSourceLabels,
      refreshYear,
      showAddExternalIdModal,
      externalIdInput,
      isAddingExternalId,
      addExternalId,
      deleteExternalId,
      showExternalCandidates,
      externalCandidates,
      artistExternalIds,
      externalTitle,
      candidatesModalTitle,
      candidateUrl,
      syncExternalId,
      selectExternalCandidate,
      platformStatsSyncing,
      syncPlatformStats,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
